/**
 * 🚀 УМНАЯ СИСТЕМА ПРЕДЗАГРУЗКИ РЕСУРСОВ
 * - Автоматическое определение chunk файлов
 * - Динамическая предзагрузка критических ресурсов
 * - Оптимизация для различных устройств
 */

class SmartPreloader {
  constructor() {
    this.preloadedResources = new Set();
    this.isSlowConnection = this.detectSlowConnection();
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  detectSlowConnection() {
    if (!navigator.connection) return false;
    
    const connection = navigator.connection;
    return connection.effectiveType === 'slow-2g' || 
           connection.effectiveType === '2g' || 
           connection.saveData === true ||
           connection.downlink < 1.5;
  }

  /**
   * Извлекает имена chunk файлов из DOM
   */
  getChunkFiles() {
    const chunks = {
      css: [],
      js: []
    };

    // Получаем CSS chunks
    document.querySelectorAll('link[rel="stylesheet"][href*="static/css/"]').forEach(link => {
      const href = link.getAttribute('href');
      if (href && !chunks.css.includes(href)) {
        chunks.css.push(href);
      }
    });

    // Получаем JS chunks
    document.querySelectorAll('script[src*="static/js/"]').forEach(script => {
      const src = script.getAttribute('src');
      if (src && !chunks.js.includes(src)) {
        chunks.js.push(src);
      }
    });

    return chunks;
  }

  /**
   * Предзагружает критические ресурсы
   */
  preloadCriticalResources() {
    if (this.isSlowConnection) {
      console.log('🚀 Slow connection detected, skipping aggressive preloading');
      return;
    }

    const chunks = this.getChunkFiles();
    
    // Предзагружаем vendor chunk (он самый тяжелый и кешируется дольше всего)
    const vendorChunk = chunks.js.find(src => src.includes('vendors'));
    if (vendorChunk && !this.preloadedResources.has(vendorChunk)) {
      this.preloadResource(vendorChunk, 'script', 'high');
    }

    // Предзагружаем основной CSS
    const mainCss = chunks.css.find(src => src.includes('main'));
    if (mainCss && !this.preloadedResources.has(mainCss)) {
      this.preloadResource(mainCss, 'style', 'high');
    }
  }

  /**
   * Предзагружает изображения портфолио
   */
  preloadPortfolioImages(imageUrls = []) {
    if (this.isSlowConnection || this.isMobile) return;

    const limit = this.isMobile ? 3 : 6; // Ограничиваем количество для мобильных
    
    imageUrls.slice(0, limit).forEach((url, index) => {
      setTimeout(() => {
        this.preloadResource(url, 'image', 'low');
      }, index * 200); // Задержка между предзагрузками
    });
  }

  /**
   * Предзагружает ресурс
   */
  preloadResource(url, as, importance = 'low') {
    if (this.preloadedResources.has(url)) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    link.as = as;
    
    if (importance === 'high') {
      link.importance = 'high';
    }

    // Обработка ошибок
    link.onerror = () => {
      console.warn('🚀 Preload failed:', url);
      this.preloadedResources.delete(url);
    };

    link.onload = () => {
      console.log('🚀 Preloaded:', url);
    };

    document.head.appendChild(link);
    this.preloadedResources.add(url);
  }

  /**
   * Предзагрузка на основе пользовательского поведения
   */
  setupBehaviorBasedPreloading() {
    // Предзагрузка при наведении на навигацию
    const navLinks = document.querySelectorAll('nav a, .nav-link');
    navLinks.forEach(link => {
      let timeout;
      
      link.addEventListener('mouseenter', () => {
        timeout = setTimeout(() => {
          const href = link.getAttribute('href');
          if (href && href.startsWith('/')) {
            this.preloadRouteAssets(href);
          }
        }, 100);
      });
      
      link.addEventListener('mouseleave', () => {
        if (timeout) clearTimeout(timeout);
      });
    });

    // Предзагрузка при скролле
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      
      scrollTimeout = setTimeout(() => {
        const scrollPercent = window.scrollY / (document.body.scrollHeight - window.innerHeight);
        
        // Когда пользователь проскроллил 30%, начинаем предзагружать следующую страницу
        if (scrollPercent > 0.3) {
          this.preloadNextPageResources();
        }
      }, 150);
    });
  }

  /**
   * Предзагружает ресурсы для определенного маршрута
   */
  preloadRouteAssets(route) {
    const routeAssets = {
      '/shop': ['/api/shop', 'shop-images'],
      '/training': ['/api/training/lessons', 'training-images'],
      '/account': ['/api/user/profile']
    };

    const assets = routeAssets[route];
    if (assets) {
      assets.forEach(asset => {
        if (asset.startsWith('/api/')) {
          // Предзагружаем API данные
          fetch(asset, { 
            method: 'GET',
            headers: { 'Priority': 'u=5' } // Low priority
          }).catch(() => {}); // Игнорируем ошибки предзагрузки
        }
      });
    }
  }

  /**
   * Предзагружает ресурсы следующей страницы
   */
  preloadNextPageResources() {
    const currentPath = window.location.pathname;
    
    // Логика определения следующей страницы на основе текущей
    const nextPageMap = {
      '/': '/shop',
      '/shop': '/training',
      '/training': '/account'
    };
    
    const nextPage = nextPageMap[currentPath];
    if (nextPage) {
      this.preloadRouteAssets(nextPage);
    }
  }

  /**
   * Инициализация системы предзагрузки
   */
  initialize() {
    // Ждем полной загрузки страницы
    if (document.readyState === 'complete') {
      this.startPreloading();
    } else {
      window.addEventListener('load', () => {
        this.startPreloading();
      });
    }
  }

  startPreloading() {
    console.log('🚀 Smart Preloader initialized');
    console.log('📊 Connection:', this.isSlowConnection ? 'Slow' : 'Fast');
    console.log('📱 Device:', this.isMobile ? 'Mobile' : 'Desktop');

    // Небольшая задержка чтобы не мешать основной загрузке
    setTimeout(() => {
      this.preloadCriticalResources();
      this.setupBehaviorBasedPreloading();
    }, 1000);
  }
}

// Создаем глобальный экземпляр
window.smartPreloader = new SmartPreloader();

export default window.smartPreloader;